import { QueryConfig, useMutation, useQuery } from 'react-query';

import { useClient } from '@context/auth';
import { IFieldsAndFilters } from '@models/common/api-client';
import { ErrorType } from '@models/common/async-hook';
import {
  CheckMembershipBalanceResponse,
  CheckMembershipsBalancePayload,
} from '@models/common/billing-card';
import {
  CancelReasonsPayload,
  FetchMembershipsPayload,
  PurchaseCreditsPayload,
  PurchaseMembershipsPayload,
  PurchaseMembershipsResponse,
} from '@models/memberships/become-member';
import { PricingModel } from '@models/memberships/membership';
import { MutationResponse, QueryResponse } from '@models/react-query';
import { client } from '@services/api-client';
import { parseFilters } from '@utils/api';

const fetchMemberships = (
  params: IFieldsAndFilters<Partial<PricingModel> & FetchMembershipsPayload>,
): Promise<PricingModel[]> => {
  const { filters } = params;
  const allFilters = {
    sortField: 'priceInCents',
    sortOrder: 'asc',
    'class.in': filters?.['class.in'] || 'credits,memberships',
    ...filters,
  };

  return client<PricingModel, undefined>(
    `products?filters=${parseFilters(allFilters, '%26')}`,
  );
};

const useFetchMemberships = (
  type: string,
  filters: FetchMembershipsPayload,
  config: QueryConfig<PricingModel[], ErrorType | null> = {},
): QueryResponse<PricingModel[]> =>
  useQuery({
    queryKey: `${type}-memberships`,
    queryFn: () =>
      fetchMemberships({
        filters: { 'class.in': 'credits,memberships', ...filters },
      }),
    config: { ...config },
  });

const useCancelMembership = (
  membershipId: string,
  config: QueryConfig<CancelReasonsPayload, ErrorType | null> = {},
): MutationResponse<CancelReasonsPayload> => {
  const authenticatedClient = useClient();

  return useMutation(
    (cancellationReason: CancelReasonsPayload) =>
      authenticatedClient(`membership/${membershipId}/cancel`, {
        method: 'POST',
        data: cancellationReason,
      }),
    { ...config },
  );
};

const usePostCancellationReasons = (
  membershipId: string,
  config: QueryConfig<CancelReasonsPayload, ErrorType | null> = {},
): MutationResponse<CancelReasonsPayload> => {
  const authenticatedClient = useClient();

  return useMutation(
    (payload: CancelReasonsPayload) =>
      authenticatedClient(`membership/${membershipId}/cancelReason`, {
        method: 'POST',
        data: payload,
      }),
    { ...config },
  );
};

const usePurchaseMembership = (
  config: QueryConfig<PurchaseMembershipsResponse, ErrorType | null> = {},
): MutationResponse<
  PurchaseMembershipsResponse,
  PurchaseMembershipsPayload
> => {
  const authenticatedClient = useClient();

  return useMutation(
    (payload: PurchaseMembershipsPayload) =>
      authenticatedClient<
        PurchaseMembershipsResponse,
        PurchaseMembershipsPayload
      >('membership', {
        method: 'POST',
        data: payload,
      }),
    { ...config },
  );
};

const usePurchaseCredits = (
  config: QueryConfig<PurchaseCreditsPayload, ErrorType | null> = {},
): MutationResponse<PurchaseCreditsPayload> => {
  const authenticatedClient = useClient();

  return useMutation(
    (payload: PurchaseCreditsPayload) =>
      authenticatedClient('credits/purchase', {
        method: 'POST',
        data: payload,
      }),
    { ...config },
  );
};

const checkForMembershipsBalance = (
  authenticatedClient: typeof client,
  params: CheckMembershipsBalancePayload,
): Promise<CheckMembershipBalanceResponse> => {
  const { bookingRefId, locationId, forGuest, serviceId } = params;
  return authenticatedClient<CheckMembershipBalanceResponse, undefined>(
    `credits/check?bookingRefId=${bookingRefId}&locationId=${locationId}&forGuest=${forGuest}&serviceId=${serviceId}`,
  );
};

const useCheckMemberships = (
  payload: CheckMembershipsBalancePayload,
  config: QueryConfig<CheckMembershipBalanceResponse, ErrorType | null> = {},
): QueryResponse<CheckMembershipBalanceResponse> => {
  const authenticatedClient = useClient();
  return useQuery({
    queryKey: 'memberships-check',
    queryFn: () => checkForMembershipsBalance(authenticatedClient, payload),
    config: { ...config },
  });
};

export {
  useFetchMemberships,
  fetchMemberships,
  useCancelMembership,
  usePostCancellationReasons,
  usePurchaseMembership,
  usePurchaseCredits,
  useCheckMemberships,
};
